import { Millwright } from '@/apis/millwrights/types/millwrightsApi';
import { Pagination, Search } from '@/components';
import { SPINNER_SIZE, TABLET_WIDTH } from '@/constants';
import {
  LoadingSpinner,
  Stack,
  Typography,
  useWindowSize,
} from '@data-products-and-ai/react-components';
import { FC, ReactNode } from 'react';
import { MillwrightCard } from '@/pages/Millwrights/components';

import '@/pages/Millwrights/components/MillwrightsList/MillwrightsList.scss';

interface MillwrightsListParams {
  list: Millwright[];
  totalItems: number;
  totalPages: number;
  currentPage?: number;
  hasPagination?: boolean;
  loading: boolean;
  clickable?: boolean;
  header?: string;
  hasSearch?: boolean;
  searchQuery?: string;
  sortComponent?: ReactNode;
  onSearchDone?: (searchTerm: string) => void;
  onPageChange?: (page: number) => void;
}

export const MillwrightsList: FC<MillwrightsListParams> = ({
  list,
  loading,
  header,
  totalItems,
  totalPages,
  clickable = true,
  hasPagination = false,
  currentPage = 1,
  hasSearch = false,
  searchQuery = '',
  sortComponent,
  onSearchDone,
  onPageChange,
}) => {
  const { width } = useWindowSize();
  const isSmallerScreen = width <= TABLET_WIDTH;

  const handleSearch = (value: string) => {
    onSearchDone?.(value);
  };

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;

    onPageChange?.(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="millwrights-list">
      <div className="millwrights-list__header">
        <div className="millwrights-list__title">
          <Typography color="var(--color-black)" tag="h3">
            {header ?? 'Millwrights'}
          </Typography>
          <Typography color="var(--color-default)" tag="textdefault">
            {totalItems ?? 0}
          </Typography>
        </div>
        {sortComponent && (
          <div className="millwrights-list__sort">{sortComponent}</div>
        )}
        {hasSearch && (
          <div className="millwrights-list__search">
            <Search
              placeholder="Search millwrights..."
              value={searchQuery}
              onSearch={handleSearch}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="millwrights-list__spinner">
          <LoadingSpinner
            size={SPINNER_SIZE}
            text="Loading millwrights..."
            color="var(--color-primary)"
            align="center"
            tag="textdefault"
            inline
          />
        </div>
      ) : list.length > 0 ? (
        <div className="millwrights-list__cards">
          {list.map((millwright) => (
            <MillwrightCard
              clickable={clickable}
              key={millwright.empId}
              millwright={millwright}
            />
          ))}
        </div>
      ) : (
        <Stack>
          <Typography color="var(--color-default)" tag="textdefault">
            No millwrights found
          </Typography>
        </Stack>
      )}
      {hasPagination && !loading && (
        <Stack>
          <Pagination
            disabled={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            maxVisiblePages={isSmallerScreen ? 3 : 5}
            onPageChange={handlePageChange}
          />
        </Stack>
      )}
    </div>
  );
};
